import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatSort } from '@angular/material/sort';
import { MatSelect } from '@angular/material/select';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, skip, switchMap, take, tap } from 'rxjs/operators';
import { utc } from 'moment';

import {
  ContactRelationshipsService,
  DisplayNoteMessage,
  Note,
  NoteDataSource,
  NoteEntity,
  NoteEntityType,
  NoteMode,
  NoteService,
  RequestNotePage,
} from '../../../../../../core/lists';
import { currentUser } from '../../../../../../core/auth';
import { AppState } from '../../../../../../core/reducers';
import { ContactsService, TaskService } from '../../../../../../core/timeline';
import { MessageType, QueryParamsModel } from '../../../../../../core/_base/crud';
import { FollowUpService } from '../../../../../../core/services/follow-up.service';
import { FilteredViewService } from '../../../../../../core/services/filtered-view.service';
import { GeneralAutocompleteComponent } from '../../../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { TaskMode, TaskPriority, TaskStatus } from '../../../../calendar/enums';
import { CreateTaskComponent } from '../../../../calendar/create-task/create-task.component';
import { AddNoteDialogComponent } from '../add-note-dialog/add-note-dialog.component';
import { UserService } from '../../../../../../core/auth/_services';
import { ActivatedRoute } from '@angular/router';
import { DownloadService } from '../../../../../../shared/utils/download.service';

@Component({
  selector: 'kt-note-list',
  templateUrl: './note-list.component.html',
  styleUrls: ['./note-list.component.scss'],
})
export class NoteListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  dataSource: NoteDataSource;
  notesResult = [];
  displayedCols = [
    'pinned',
    'entities',
    'contacts',
    'subject',
    'description',
    'created',
    'created_by',
    'updated',
    'updated_by',
    'actions',
  ];
  searchBarCols = [
    'searchPinned',
    'searchEntity',
    'searchContact',
    'searchSubject',
    'searchDescription',
    'searchStarted',
    'searchStartedBy',
    'searchUpdated',
    'searchUpdatedBy',
    'searchActions',
  ];
  showSearchBar = false;
  exporting = false;
  getEntityAutocomplete$ = this.relationshipsService.findEntities.bind(this.relationshipsService, {
    entityTypes: Object.values(NoteEntityType).filter((t) => t !== NoteEntityType.CONTACT),
  });
  getContactAutocomplete$ = this.relationshipsService.findEntities.bind(this.relationshipsService, {
    entityTypes: [NoteEntityType.CONTACT],
  });
  getUserAutocomplete$ = this.userService.searchUsers.bind(this.userService);
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchPinnedSelect') searchPinnedSelect: MatSelect;
  @ViewChild('searchEntityAutocomplete') searchEntityAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchContactAutocomplete') searchContactAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchUserCreatedAutocomplete')
  searchUserCreatedAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchUserUpdatedAutocomplete')
  searchUserUpdatedAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('searchSubjectInput') searchSubjectInput: ElementRef;
  @ViewChild('searchDescriptionInput') searchDescriptionInput: ElementRef;
  @ViewChild('searchStartedInput') searchStartedInput: ElementRef;
  @ViewChild('searchUpdatedInput') searchUpdatedInput: ElementRef;
  @Input() entityId: number;
  @Input() entityType: NoteEntityType;
  @Output() totalCount = new EventEmitter<number>();
  @Output() pinUnpin = new EventEmitter<void>();
  usersObj: any;

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private translate: TranslateService,
    private noteService: NoteService,
    private taskService: TaskService,
    private userService: UserService,
    private contactsService: ContactsService,
    private followUpService: FollowUpService,
    private filteredViewService: FilteredViewService,
    private relationshipsService: ContactRelationshipsService,
    private downloadService: DownloadService,
    private router: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.dataSource = new NoteDataSource(this.store, this.paginator);
    // Init DataSource
    const usersSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((res) => {
        this.notesResult = res;
        const ids = res.flatMap((x) => [x.created_by, x.updated_by]);
        this.taskService.getUsersbyId([...new Set(ids.flat())]).subscribe((result) => {
          this.usersObj = result.pageOfItems;
        });
      });
    this.subscriptions.add(usersSubscription);
    localStorage.setItem('fromNoteList', 'true');
    this.dataSource.hasItems = this.notesResult.length > 0;

    if (!this.entityId && this.router.snapshot.paramMap.has('id')) {
      // tslint:disable-next-line:radix
      this.entityId = Number.parseInt(this.router.snapshot.paramMap.get('id'));
    } else if (!this.entityId && !!localStorage.getItem('notesEntityId')) {
      this.entityId = +localStorage.getItem('notesEntityId');
      localStorage.removeItem('notesEntityId');
    }

    if (!this.entityType && !!localStorage.getItem('notesEntityType')) {
      this.entityType = NoteEntityType[localStorage.getItem('notesEntityType')];
      localStorage.removeItem('notesEntityType');
    }

    this.subscriptions.add(this.dataSource.entitySubject.subscribe());
    this.subscriptions.add(
      this.dataSource.paginatorTotal$.subscribe((c) => this.totalCount.emit(c)),
    );

    this.subscriptions.add(
      this.sort.sortChange.subscribe((res) => {
        this.paginator.pageIndex = 0;

        if (!res.direction) {
          this.sort.direction = '';
          this.sort.active = '';
        }
      }),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadNoteList()))
        .subscribe(),
    );

    this.subscriptions.add(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(() => this.reloadNoteList()),
        )
        .subscribe(),
    );

    if (!!this.entityId && this.entityType === NoteEntityType.PATIENT)
      this.subscriptions.add(
        this.filteredViewService
          .createRecentSession({
            entity_id: this.entityId,
            access_from: 'Note List',
            entity_type: NoteEntityType.PATIENT,
          })
          .subscribe(),
      );

    setTimeout(() => this.loadNoteList());
  }

  ngOnDestroy(): void {
    localStorage.removeItem('fromNoteList');
    this.subscriptions.unsubscribe();
  }

  @HostListener('window:beforeunload')
  saveEntityData(): Promise<boolean> | boolean {
    localStorage.removeItem('fromNoteList');
    localStorage.setItem('notesEntityId', `${this.entityId || 0}`);
    localStorage.setItem('notesEntityType', `${this.entityType || ''}`);

    return true;
  }

  getFilterConfig() {
    const filter: any = {};

    filter.mainId = this.entityId;
    filter.mainType = this.entityType;

    if (this.showSearchBar) {
      filter.pinned = this.searchPinnedSelect?.value;
      filter.entityId = this.searchEntityAutocomplete?.value?.id;
      filter.contactId = this.searchContactAutocomplete?.value?.id;
      filter.pinned = this.searchPinnedSelect?.value;
      filter.subject = this.searchSubjectInput?.nativeElement?.value;
      filter.description = this.searchDescriptionInput?.nativeElement?.value;
      filter.created = this.datePipe.transform(
        this.searchStartedInput?.nativeElement?.value,
        'yyyy-MM-dd',
      );
      filter.updated = this.datePipe.transform(
        this.searchUpdatedInput?.nativeElement?.value,
        'yyyy-MM-dd',
      );
      filter.createdBy = this.searchUserCreatedAutocomplete?.value?.id;
      filter.updatedBy = this.searchUserUpdatedAutocomplete?.value?.id;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
    }

    return filter;
  }

  loadNoteList() {
    const query = new QueryParamsModel(
      this.getFilterConfig(),
      this.sort.direction,
      this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.store.dispatch(new RequestNotePage({ query }));
  }

  formatEntityOption(entity: NoteEntity): string {
    return entity?.id
      ? `${entity.name || `${entity.first_name || ''} ${entity.last_name || ''}`}${
          entity.entity_type
            ? `<small class="text-muted ml-2">(${entity.entity_type || ''})</small>`
            : ''
        }`.trim()
      : '';
  }

  formatEntityValue(entity: NoteEntity): string {
    return entity?.id
      ? `${entity.name || `${entity.first_name || ''} ${entity.last_name || ''}`}`.trim()
      : '';
  }

  getEntitiesString(entities: NoteEntity[], startIdx = 0): string {
    return entities
      .slice(startIdx)
      .map((e) => this.formatEntityValue(e))
      .join(', ');
  }

  addNote(event: Event): void {
    event.stopPropagation();

    const getEntitiesAPI$ =
      this.entityType === NoteEntityType.CONTACT
        ? this.contactsService.getContactsByIds([this.entityId])
        : this.relationshipsService.getEntitiesById([this.entityId]);

    getEntitiesAPI$.pipe(take(1)).subscribe((res) => {
      if (!res?.data) return;

      const note = new Note();

      if (this.entityType === NoteEntityType.CONTACT)
        note.contacts = res.data as unknown as NoteEntity[];
      else note.entities = res.data as unknown as NoteEntity[];

      this.updateNote(null, note);
    });
  }

  updateNote(event: Event, note: Note): void {
    event?.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '95vw';
    dialogConfig.maxWidth = '95vw';
    dialogConfig.data = {
      mode: note.id ? NoteMode.UPDATE : NoteMode.CREATE,
      fromContact: this.entityType === NoteEntityType.CONTACT,
      note,
    };

    const dialogRef = this.dialog.open(AddNoteDialogComponent, dialogConfig);

    this.subscriptions.add(
      dialogRef.afterClosed().subscribe((res) => {
        if (!res?.note) return;

        this.pinUnpin.emit();

        this.store.dispatch(
          new DisplayNoteMessage({
            message: this.translate.instant(
              `NOTE.${note.id ? 'UPDATE_MESSAGE' : 'CREATE_MESSAGE'}`,
            ),
            messageType: note.id ? MessageType.Update : MessageType.Create,
          }),
        );

        if (res.note.createTask) setTimeout(() => this.createTask(null, res.note), 1000);
      }),
    );
  }

  pinUnpinNote(event: Event, note: Note): void {
    event.stopPropagation();

    this.subscriptions.add(
      this.noteService
        .pinUnpinNote(note)
        .pipe(take(1))
        .subscribe((res) => {
          if (!res?.ok) return;

          this.pinUnpin.emit();

          this.store.dispatch(
            new DisplayNoteMessage({
              message: this.translate.instant(
                note.pinned ? 'NOTE.UNPIN_MESSAGE' : 'NOTE.PIN_MESSAGE',
              ),
              messageType: MessageType.Update,
            }),
          );
        }),
    );
  }

  openFollowUpDialog(event: Event, note: Note): void {
    event.stopPropagation();

    this.followUpService.openDialog({
      type: 'NOTE',
      title: note.subject,
      relatedIds: note.entities?.map((e) => e.id) || [],
      body: { id: note.id },
    });
  }

  createTask(event: Event, note: Note): void {
    event?.stopPropagation();

    this.subscriptions.add(
      this.store
        .pipe(
          select(currentUser),
          switchMap((user) =>
            this.taskService.createTaskV3({
              id: null,
              task_entities: note.entities?.map((e) => ({ entity_id: e.id })) || [],
              task_tags: [],
              task_attachments: [],
              steps: [
                {
                  id: null,
                  title: note.subject,
                  order: 1,
                  default_view: null,
                  priority: TaskPriority.LOW,
                  progress: TaskStatus.PLANNED,
                  office_id: null,
                  description: note.description,
                  url_resource: null,
                  due_date: null,
                  sequential_order: false,
                  task_template: null,
                  task_followers: [{ follower_id: user?.id }],
                  task_responsible_users: [],
                  blockers: null,
                  check_list: null,
                  intervention: null,
                  transaction_form: null,
                },
              ],
            }),
          ),
          take(1),
        )
        .subscribe((res) => {
          if (!res?.data) return;

          this.store.dispatch(
            new DisplayNoteMessage({
              message: this.translate.instant('NOTE.CREATE_TASK_MESSAGE'),
              messageType: MessageType.Create,
            }),
          );

          setTimeout(() => {
            const dialogConfig = new MatDialogConfig();

            dialogConfig.disableClose = true;
            dialogConfig.closeOnNavigation = true;
            dialogConfig.width = '95vw';
            dialogConfig.maxWidth = '95vw';
            dialogConfig.data = { mode: TaskMode.UPDATE, task: res.data };

            this.dialog.open(CreateTaskComponent, dialogConfig);
          }, 1000);
        }),
    );
  }

  exportNotesAsCSV(event: Event): void {
    event.stopPropagation();

    this.exporting = true;

    const query = new QueryParamsModel(
      this.getFilterConfig(),
      this.sort.direction,
      this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.subscriptions.add(
      this.noteService
        .getNotesAsCSV(query)
        .pipe(take(1))
        .subscribe((res) => {
          if (!res) return;
          const filename = `Notes List [${utc(new Date())
            .local()
            .format('YYYY-MM-DD, hh.mm a')}].csv`;
          this.downloadService.downloadFile(res, filename, 'text/csv');

          this.exporting = false;
          this.store.dispatch(
            new DisplayNoteMessage({
              message: this.translate.instant('NOTE.EXPORT_CSV_MESSAGE'),
              messageType: MessageType.Create,
            }),
          );
        }),
    );
  }

  reloadNoteList(): void {
    this.paginator.pageIndex = 0;

    this.loadNoteList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchPinnedSelect.value = '';
    this.searchEntityAutocomplete?.setValue(null);
    this.searchEntityAutocomplete?.updateValueAndValidity();
    this.searchContactAutocomplete?.setValue(null);
    this.searchContactAutocomplete?.updateValueAndValidity();
    this.searchUserCreatedAutocomplete?.setValue(null);
    this.searchUserCreatedAutocomplete?.updateValueAndValidity();
    this.searchUserUpdatedAutocomplete?.setValue(null);
    this.searchUserUpdatedAutocomplete?.updateValueAndValidity();
    this.searchSubjectInput.nativeElement.value = '';
    this.searchDescriptionInput.nativeElement.value = '';
    this.searchStartedInput.nativeElement.value = '';
    this.searchUpdatedInput.nativeElement.value = '';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadNoteList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchPinnedSelect.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadNoteList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadNoteList();
  }
  getUser(id: any) {
    return this.usersObj?.find((e) => e.id === id);
  }

  formatUserValue(user) {
    let res = '';
    if (user) {
      res += user.fullName;
    }
    return res;
  }
}
